import {Body_send_support, SupportCategory, Theme} from '../../types';
import {Box, IconButton, Typography, makeStyles, useTheme} from '@mui/material';
import {
    CategorySelect,
    EmailInput,
    MessageInput,
    SubjectInput,
} from '../../components/common/input';
import {Formik, FormikHelpers, FormikProps} from 'formik';
import {NotificationSeverity, useNotification} from '../../provider/NotificationProvider';
import {getFileName, getSize} from '../../utils/tool';
import {useEffect, useMemo, useRef, useState} from 'react';
import yup, {YupTypes, yupRules} from '../../utils/yup';

import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import {Colors} from '../common/colors';
import {CustomButton} from '../common/button';
import WithTranslateFormErrors from '../../utils/useTranslateFormErrors';
import {getLocaleLabels} from '../../utils/lang';
import {useResponsive} from '../../provider/MediaQueryProvider';
import {useSendSupport} from '../../queries/helpQueries';
import {useTranslation} from 'next-i18next';
import {useUser} from '../../provider/UserProvider';

type Inputs = Pick<YupTypes, 'email' | 'category' | 'subject' | 'message'>;
type Helpers = FormikHelpers<Inputs>;
type Props = FormikProps<Inputs>;

const initialValues = {
    email: '',
    category: 1,
    subject: '',
    message: '',
};

type SupportFormProps = {
    categories: SupportCategory[];
    campaignId?: string | null | undefined;
    theme?: Theme | null | undefined;
    onSubmitted?: () => void;
};

const MAX_FILE_SIZE = 1024 * 1024 * 5;
const MAX_FILE_NB = 3;

export default function SupportForm(props: SupportFormProps) {
    const {dbUser, googleUser} = useUser();
    const formRef = useRef<FormikProps<typeof initialValues>>();
    const email = useMemo(() => dbUser?.email || googleUser?.email, [dbUser, googleUser]);
    useEffect(() => {
        if (email && !formRef.current?.values.email && !formRef.current?.touched.email) {
            formRef.current?.setFieldValue('email', email);
        }
    }, [email]);

    const theme = useTheme();
    const isDark = theme.palette.mode == 'dark';
    const {isMobile, isTablet} = useResponsive();
    const {display} = useNotification();
    const data = props.categories;
    const sendSupport = useSendSupport();
    const i18n = useTranslation('common');
    const [files, setFiles] = useState<File[]>([]);

    const validationSchema = useMemo(() => {
        const {email, category, subject, message} = yupRules(i18n);
        const validationSchema = yup.object().shape({email, category, subject, message});
        return validationSchema;
    }, [i18n]);

    async function sendSupportEmail(
        values: Inputs,
        {resetForm, setErrors, setSubmitting}: Helpers
    ) {
        try {
            const supportCategory = data.find((f) => f.order == values.category);
            const locale = getLocaleLabels(supportCategory.labels, i18n.i18n.language);
            const body = {
                category: locale.label,
                support_category_id: supportCategory.support_category_id,
                email: values.email,
                subject: values.subject,
                message: values.message,
                campaign_id: props.campaignId,
                files: files,
            } as Body_send_support;

            await sendSupport.mutateAsync(body);
            display(i18n.t('display.send') + ' 😇');
            resetForm();
            setFiles([]);
            props.onSubmitted?.();
        } catch (e) {
            console.log(e);
        }
    }

    async function addFile() {
        const input = document.getElementById('attachment-file') as HTMLInputElement;
        let inputFiles = [];
        let n = files.length;
        for (var i = 0; i < input.files.length; i++) {
            let file = input.files[i];

            if (file.size >= MAX_FILE_SIZE) {
                display(i18n.t('warning.weight_max'), NotificationSeverity.WARNING);
                break;
            }

            if (n >= MAX_FILE_NB) {
                display(i18n.t('warning.file_max'), NotificationSeverity.WARNING);
                break;
            }

            inputFiles.push(file);
            n++;
        }
        const newFiles = [...files, ...inputFiles];
        setFiles(newFiles);
    }

    async function removeFile(f: File) {
        const newFiles = files.filter((s) => s != f);
        setFiles(newFiles);
    }

    if (!data) {
        return <></>;
    }

    const flexDirection = isTablet || isMobile ? 'column' : 'row';
    const color = props.theme?.background_color ?? Colors.first;

    return (
        <Formik
            innerRef={(f) => (formRef.current = f)}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={sendSupportEmail}
            validateOnChange
        >
            {(formik: Props) => (
                <WithTranslateFormErrors
                    errors={formik.errors}
                    touched={formik.touched}
                    setFieldTouched={formik.setFieldTouched}
                    validationSchema={validationSchema}
                >
                    <form id="support" autoComplete="on" onSubmit={formik.handleSubmit}>
                        <Box
                            display={'flex'}
                            flex="1"
                            flexDirection="column"
                            alignItems="center"
                        >
                            <Box
                                display="flex"
                                flex="1"
                                flexDirection={flexDirection}
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <Box
                                    display={'flex'}
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    marginRight={isTablet || isMobile ? '0px' : '40px'}
                                >
                                    <EmailInput
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.email &&
                                            Boolean(formik.errors.email)
                                        }
                                        helperText={
                                            formik.touched.email && formik.errors.email
                                        }
                                        tips={false}
                                        sx={{
                                            width: '290px',
                                            '& label.Mui-focused': {
                                                color: color,
                                            },
                                            '& .Mui-focused .MuiOutlinedInput-notchedOutline':
                                                {
                                                    borderColor: `${color} !important`,
                                                },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: `${color} !important`,
                                            },
                                            '& .Mui-focused.MuiOutlinedInput-notchedOutline': {
                                                borderColor: `${color} !important`,
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: `${color} !important`,
                                            },
                                            '&.Mui-focused.MuiOutlinedInput-notchedOutline:hover':
                                                {
                                                    borderColor: `${color} !important`,
                                                },
                                            backgroundColor: isDark
                                                ? Colors.dark
                                                : 'transparent',
                                        }}
                                    />
                                    <CategorySelect
                                        value={formik.values.category}
                                        onChange={(event) => {
                                            formik.setFieldValue(
                                                'category',
                                                event.target.value
                                            );
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.category &&
                                            Boolean(formik.errors.category)
                                        }
                                        helper={
                                            formik.touched.category && formik.errors.category
                                        }
                                        categories={data}
                                        sx={{
                                            width: '290px',
                                        }}
                                        theme={props.theme}
                                    />
                                    <SubjectInput
                                        value={formik.values.subject}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.subject &&
                                            Boolean(formik.errors.subject)
                                        }
                                        helperText={
                                            formik.touched.subject && formik.errors.subject
                                        }
                                        sx={{
                                            width: '290px',
                                            '& label.Mui-focused': {
                                                color: color,
                                            },
                                            '& .Mui-focused .MuiOutlinedInput-notchedOutline':
                                                {
                                                    borderColor: `${color} !important`,
                                                },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: `${color} !important`,
                                            },
                                            '& .Mui-focused.MuiOutlinedInput-notchedOutline': {
                                                borderColor: `${color} !important`,
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: `${color} !important`,
                                            },
                                            '&.Mui-focused.MuiOutlinedInput-notchedOutline:hover':
                                                {
                                                    borderColor: `${color} !important`,
                                                },
                                            backgroundColor: isDark
                                                ? Colors.dark
                                                : 'transparent',
                                        }}
                                    />
                                </Box>
                                <MessageInput
                                    value={formik.values.message}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.message &&
                                        Boolean(formik.errors.message)
                                    }
                                    helperText={
                                        formik.touched.message && formik.errors.message
                                    }
                                    // onKeyUp={(e) => {
                                    //     if (e.key === 'Enter') {
                                    //         formik.handleSubmit();
                                    //     }
                                    // }}
                                    multiline={true}
                                    sx={{
                                        width: '290px',
                                        '& textarea': {height: '250px !important'},
                                        '& label.Mui-focused': {
                                            color: color,
                                        },
                                        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: `${color} !important`,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: `${color} !important`,
                                        },
                                        '& .Mui-focused.MuiOutlinedInput-notchedOutline': {
                                            borderColor: `${color} !important`,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: `${color} !important`,
                                        },
                                        '&.Mui-focused.MuiOutlinedInput-notchedOutline:hover':
                                            {
                                                borderColor: `${color} !important`,
                                            },
                                        backgroundColor: isDark ? Colors.dark : 'transparent',
                                    }}
                                />
                            </Box>
                            <CustomButton
                                onClick={() =>
                                    document.getElementById('attachment-file').click()
                                }
                                startIcon={<AttachFileIcon />}
                                sx={{
                                    marginTop: '20px',
                                    border: 'none',
                                    '&:hover': {
                                        boxShadow: 'none',
                                        border: 'none',
                                        background: 'none',
                                        textDecoration: 'underline',
                                    },
                                }}
                            >
                                {i18n.t('action.add_file')}
                                <input
                                    hidden
                                    accept="image/*"
                                    multiple
                                    type="file"
                                    id="attachment-file"
                                    onChange={() => addFile()}
                                />
                            </CustomButton>
                            <Box textAlign="center" marginTop="5px" marginBottom="5px">
                                {files.map((f) => (
                                    <Box
                                        display="flex"
                                        flexDirection="row"
                                        justifyContent="center"
                                        key={f.name}
                                    >
                                        <Typography
                                            sx={{
                                                maxWidth: '300px',
                                                fontSize: '10px',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {getFileName(f.name)} ({getSize(f.size)})
                                        </Typography>
                                        <IconButton
                                            aria-label="close"
                                            onClick={() => removeFile(f)}
                                            sx={{
                                                margin: '0px',
                                                padding: '0px',
                                            }}
                                        >
                                            <CloseIcon
                                                sx={{
                                                    fontSize: '10px',
                                                    marginLeft: '5px',
                                                }}
                                            />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                            <CustomButton
                                onClick={() => formik.handleSubmit()}
                                disabled={!formik.isValid}
                                sx={{
                                    marginTop: '25px',
                                    color: 'primary',
                                    borderColor: color,
                                    backgroundColor: 'transparent',
                                    '&:hover': {
                                        backgroundColor: color,
                                    },
                                }}
                            >
                                {i18n.t('action.send_message')}
                            </CustomButton>
                        </Box>
                    </form>
                </WithTranslateFormErrors>
            )}
        </Formik>
    );
}
